import { Clock, FileText, Search, Eye, AlertTriangle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useSubscription } from "@/hooks/use-subscription";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

const TrialBanner = () => {
  const { 
    isTrialing, 
    daysRemaining, 
    loading, 
    hasAccess, 
    subscriptionStatus,
    scheduledCancellation,
    cancelAt,
    applicationsUsed,
    applicationsLimit,
    lookupsUsed,
    lookupsLimit,
    enrichedViewsUsed,
    enrichedViewsLimit,
    openCustomerPortal
  } = useSubscription();

  // Don't show banner if loading or no access
  if (loading || !hasAccess) {
    return null;
  }

  // Show scheduled cancellation banner for active subscriptions that are set to cancel
  // Only show when 30 days or less remain until cancellation
  if (scheduledCancellation && cancelAt) {
    const cancelDate = new Date(cancelAt);
    const now = new Date();
    const daysUntilCancellation = Math.ceil((cancelDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    // Only show banner if 30 days or less until cancellation
    if (daysUntilCancellation <= 30) {
      const formattedDate = format(cancelDate, "d MMMM yyyy", { locale: sv });
      
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg mx-auto mt-4 py-3 px-4 max-w-4xl">
          <div className="flex items-center justify-center gap-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <span>
              Din prenumeration avslutas den <strong>{formattedDate}</strong>.{" "}
              <button 
                onClick={() => openCustomerPortal()} 
                className="underline hover:text-amber-900 font-medium inline-flex items-center gap-1"
              >
                Återaktivera i kundportalen
                <ExternalLink className="h-3 w-3" />
              </button>
            </span>
          </div>
        </div>
      );
    }
  }

  // Don't show trial banner for active subscriptions
  if (subscriptionStatus === "active") {
    return null;
  }

  // Only show trial banner for users in trial period with 10 days or less remaining
  if (!isTrialing || daysRemaining === null || daysRemaining > 10) {
    return null;
  }

  const applicationsRemaining = Math.max(0, applicationsLimit - applicationsUsed);
  const lookupsRemaining = Math.max(0, lookupsLimit - lookupsUsed);
  
  // Show foundation views when user has viewed more than 200
  const showFoundationViews = enrichedViewsLimit !== null && enrichedViewsUsed > 200;
  const foundationsRemaining = enrichedViewsLimit ? Math.max(0, enrichedViewsLimit - enrichedViewsUsed) : null;

  return (
    <div className="bg-accent/10 border border-accent/20 rounded-lg mx-auto mt-4 py-3 px-4 max-w-4xl">
      <div className="flex flex-col gap-2">
        {/* Trial days remaining */}
        <div className="flex items-center justify-center gap-2 text-sm text-foreground">
          <Clock className="h-4 w-4 text-accent flex-shrink-0" />
          <span>
            Din provperiod har <strong>{daysRemaining} dagar</strong> kvar.{" "}
            <Link to="/pricing" className="underline hover:text-accent font-medium">
              Välj en plan
            </Link>{" "}
            för att fortsätta använda Bidragsassistenten.
          </span>
        </div>
        
        {/* Usage stats */}
        <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground flex-wrap">
          <div className="flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            <span>{applicationsUsed}/{applicationsLimit} ansökningar</span>
          </div>
          <span className="text-border">·</span>
          <div className="flex items-center gap-1.5">
            <Search className="h-3.5 w-3.5" />
            <span>{lookupsUsed}/{lookupsLimit} förfrågningar</span>
          </div>
          {showFoundationViews && (
            <>
              <span className="text-border">·</span>
              <div className="flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" />
                <span>{enrichedViewsUsed}/{enrichedViewsLimit} unika stiftelser visade</span>
              </div>
            </>
          )}
        </div>
        
        {/* Warning when approaching foundation view limit */}
        {showFoundationViews && foundationsRemaining !== null && foundationsRemaining <= 50 && (
          <div className="flex items-center justify-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-md py-1.5 px-3 mt-1">
            <Eye className="h-4 w-4 flex-shrink-0" />
            <span>
              Du har bara <strong>{foundationsRemaining} stiftelser</strong> kvar att visa.{" "}
              <Link to="/pricing" className="underline hover:text-amber-900 font-medium">
                Uppgradera
              </Link>{" "}
              för att få tillgång till hela databasen.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrialBanner;
